// Scheduled Reports — pick a saved template, cadence, format & recipients.
// Delivered by email (via the tenant's configured SMTP channel) on schedule.
import { useEffect, useMemo, useState } from "react";
import {
  CalendarClock, Loader2, Save, Trash2, Plus, Pencil, Send, AlertTriangle, X,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const FREQ = [
  { id: "daily", label: "Every day" },
  { id: "weekly", label: "Every week" },
  { id: "monthly", label: "Every month" },
];
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const FORMATS = [
  { id: "csv", label: "CSV" },
  { id: "xlsx", label: "Excel (.xlsx)" },
  { id: "json", label: "JSON" },
];

const EMPTY = {
  name: "", template_id: "", frequency: "daily", hour: 8, minute: 0,
  weekday: 0, day_of_month: 1, format: "csv", recipients: [], subject: "", enabled: true,
};

export default function ScheduledReportsPage() {
  const [templates, setTemplates] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [emailReady, setEmailReady] = useState(true);
  const [editing, setEditing] = useState(null);  // null | "new" | id
  const [form, setForm] = useState(EMPTY);
  const [recipInput, setRecipInput] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  async function loadAll() {
    setLoading(true);
    try {
      const [tpl, sch] = await Promise.all([
        api.get("/reports/templates"),
        api.get("/reports/schedules"),
      ]);
      setTemplates(tpl.data.templates || []);
      setSchedules(sch.data.schedules || []);
      setEmailReady(sch.data.email_configured !== false);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }

  const tplName = useMemo(() => {
    const m = {}; templates.forEach((t) => { m[t.id] = t.name; }); return m;
  }, [templates]);

  function openNew() {
    if (!templates.length) { toast.error("Create a Report Template first"); return; }
    setForm({ ...EMPTY, template_id: templates[0].id, recipients: [] });
    setRecipInput(""); setEditing("new");
  }
  function openEdit(s) {
    setForm({
      name: s.name, template_id: s.template_id || "", frequency: s.frequency || "daily",
      hour: s.hour ?? 8, minute: s.minute ?? 0, weekday: s.weekday ?? 0,
      day_of_month: s.day_of_month ?? 1, format: s.format || "csv",
      recipients: s.recipients || [], subject: s.subject || "", enabled: s.enabled !== false,
    });
    setRecipInput(""); setEditing(s.id);
  }
  function cancel() { setEditing(null); }

  function addRecipient() {
    const v = recipInput.trim();
    if (!v) return;
    if (!v.includes("@")) { toast.error("Enter a valid email"); return; }
    if (form.recipients.includes(v)) { setRecipInput(""); return; }
    setForm({ ...form, recipients: [...form.recipients, v] });
    setRecipInput("");
  }
  function removeRecipient(r) {
    setForm({ ...form, recipients: form.recipients.filter((x) => x !== r) });
  }

  async function save() {
    if (!form.name.trim()) { toast.error("Name the schedule"); return; }
    if (!form.template_id) { toast.error("Pick a template"); return; }
    if (!form.recipients.length) { toast.error("Add at least one recipient"); return; }
    const body = {
      ...form, name: form.name.trim(),
      hour: Number(form.hour), minute: Number(form.minute),
      weekday: Number(form.weekday), day_of_month: Number(form.day_of_month),
    };
    try {
      if (editing === "new") await api.post("/reports/schedules", body);
      else await api.put(`/reports/schedules/${editing}`, body);
      toast.success("Schedule saved");
      setEditing(null); loadAll();
    } catch (e) { toast.error(formatErr(e)); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this schedule?")) return;
    try { await api.delete(`/reports/schedules/${id}`); toast.success("Deleted"); loadAll(); }
    catch (e) { toast.error(formatErr(e)); }
  }

  async function runNow(s) {
    toast.loading("Sending report…", { id: "runnow" });
    try {
      const r = await api.post(`/reports/schedules/${s.id}/run-now`);
      toast.success(`Sent to ${(r.data.recipients || []).length} recipient(s)`, { id: "runnow" });
      loadAll();
    } catch (e) { toast.error(formatErr(e), { id: "runnow" }); }
  }

  async function toggleEnabled(s) {
    try {
      await api.put(`/reports/schedules/${s.id}`, {
        name: s.name, template_id: s.template_id, frequency: s.frequency,
        hour: s.hour, minute: s.minute, weekday: s.weekday, day_of_month: s.day_of_month,
        format: s.format, recipients: s.recipients, subject: s.subject,
        enabled: !(s.enabled !== false),
      });
      loadAll();
    } catch (e) { toast.error(formatErr(e)); }
  }

  const fmtNext = (iso) => iso ? new Date(iso).toLocaleString() : "—";

  return (
    <div className="px-4 sm:px-6 py-6 max-w-6xl" data-testid="scheduled-reports-page">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-primary" /> Scheduled Reports
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Email a saved report template to your team automatically — daily, weekly or monthly.
          </p>
        </div>
        {!editing && (
          <Button onClick={openNew} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5" data-testid="sr-new">
            <Plus className="w-4 h-4" /> New schedule
          </Button>
        )}
      </div>

      {!emailReady && (
        <div className="mb-5 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-4 py-3 text-sm" data-testid="sr-email-warning">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <span className="font-medium">Email delivery isn't configured.</span> Set up the{" "}
            <span className="font-medium">Email (SMTP)</span> channel in Admin → Omnichannel Hub. You can still create
            schedules, but delivery will fail until SMTP is connected.
          </div>
        </div>
      )}

      {/* Editor */}
      {editing && (
        <Card className="p-5 mb-6 space-y-4" data-testid="sr-editor">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase font-bold tracking-wider mb-1 block">Schedule name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Morning KPI to managers" data-testid="sr-name" />
            </div>
            <div>
              <Label className="text-xs uppercase font-bold tracking-wider mb-1 block">Report template</Label>
              <Select value={form.template_id} onValueChange={(v) => setForm({ ...form, template_id: v })}>
                <SelectTrigger data-testid="sr-template"><SelectValue placeholder="Pick a template" /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid sm:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs uppercase font-bold tracking-wider mb-1 block">Frequency</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                <SelectTrigger data-testid="sr-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>{FREQ.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.frequency === "weekly" && (
              <div>
                <Label className="text-xs uppercase font-bold tracking-wider mb-1 block">Day of week</Label>
                <Select value={String(form.weekday)} onValueChange={(v) => setForm({ ...form, weekday: Number(v) })}>
                  <SelectTrigger data-testid="sr-weekday"><SelectValue /></SelectTrigger>
                  <SelectContent>{WEEKDAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {form.frequency === "monthly" && (
              <div>
                <Label className="text-xs uppercase font-bold tracking-wider mb-1 block">Day of month</Label>
                <Input type="number" min={1} max={31} value={form.day_of_month}
                  onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} data-testid="sr-dom" />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase font-bold tracking-wider mb-1 block">Time (local)</Label>
              <div className="flex items-center gap-1">
                <Input type="number" min={0} max={23} value={form.hour}
                  onChange={(e) => setForm({ ...form, hour: e.target.value })} className="w-16" data-testid="sr-hour" />
                <span className="text-muted-foreground">:</span>
                <Input type="number" min={0} max={59} value={form.minute}
                  onChange={(e) => setForm({ ...form, minute: e.target.value })} className="w-16" data-testid="sr-minute" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase font-bold tracking-wider mb-1 block">Format</Label>
              <Select value={form.format} onValueChange={(v) => setForm({ ...form, format: v })}>
                <SelectTrigger data-testid="sr-format"><SelectValue /></SelectTrigger>
                <SelectContent>{FORMATS.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase font-bold tracking-wider mb-1 block">Recipients</Label>
            <div className="flex gap-2">
              <Input value={recipInput} onChange={(e) => setRecipInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }}
                placeholder="name@company.com — press Enter" className="flex-1" data-testid="sr-recip-input" />
              <Button variant="outline" onClick={addRecipient} data-testid="sr-recip-add">Add</Button>
            </div>
            {form.recipients.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {form.recipients.map((r) => (
                  <Badge key={r} variant="secondary" className="gap-1" data-testid={`sr-recip-${r}`}>
                    {r}
                    <button onClick={() => removeRecipient(r)} className="hover:text-red-600"><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase font-bold tracking-wider mb-1 block">Email subject (optional)</Label>
            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="Defaults to the template name" data-testid="sr-subject" />
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="sr-enabled" />
            <span className="text-sm">Active (runs on schedule)</span>
          </div>

          <div className="flex gap-2 pt-2 border-t border-border">
            <Button onClick={save} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5" data-testid="sr-save">
              <Save className="w-4 h-4" /> Save schedule
            </Button>
            <Button onClick={cancel} variant="ghost" data-testid="sr-cancel">Cancel</Button>
          </div>
        </Card>
      )}

      {/* List */}
      <Card className="p-5" data-testid="sr-list">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold">Schedules</h3>
          <Badge variant="outline" className="text-[10px]">{schedules.length}</Badge>
        </div>
        {loading ? (
          <div className="h-24 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : schedules.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No schedules yet — click <span className="font-medium">New schedule</span> to create one.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  {["Name", "Template", "Cadence", "Format", "Recipients", "Next run", "Last status", ""].map((h) => (
                    <th key={h} className="text-left px-3 py-2 font-semibold text-xs uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {schedules.map((s) => (
                  <tr key={s.id} className="border-t border-border/60 align-top" data-testid={`sr-row-${s.id}`}>
                    <td className="px-3 py-2 font-medium">{s.name}</td>
                    <td className="px-3 py-2">{s.template_name || tplName[s.template_id] || <span className="text-red-600">missing</span>}</td>
                    <td className="px-3 py-2 capitalize">
                      {s.frequency}{s.frequency === "weekly" ? ` · ${WEEKDAYS[s.weekday ?? 0]}` : ""}
                      {s.frequency === "monthly" ? ` · day ${s.day_of_month}` : ""}
                      {" · "}{String(s.hour ?? 8).padStart(2, "0")}:{String(s.minute ?? 0).padStart(2, "0")}
                    </td>
                    <td className="px-3 py-2 uppercase text-xs">{s.format}</td>
                    <td className="px-3 py-2 text-xs">{(s.recipients || []).length}</td>
                    <td className="px-3 py-2 text-xs">{s.enabled !== false ? fmtNext(s.next_run_at) : <span className="text-muted-foreground">paused</span>}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground max-w-[180px] truncate" title={s.last_status || ""}>{s.last_status || "—"}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 justify-end">
                        <Switch checked={s.enabled !== false} onCheckedChange={() => toggleEnabled(s)} data-testid={`sr-toggle-${s.id}`} />
                        <Button variant="outline" size="sm" onClick={() => runNow(s)} className="h-8 gap-1 text-xs" data-testid={`sr-run-${s.id}`}>
                          <Send className="w-3.5 h-3.5" /> Run now
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openEdit(s)} className="h-8 gap-1 text-xs" data-testid={`sr-edit-${s.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <button onClick={() => remove(s.id)} className="text-muted-foreground hover:text-red-600 p-1" data-testid={`sr-del-${s.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
